import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { LOGO_WHITE_URL, MENU_GROUPS, INSTAGRAM_URL } from "../config/brand";

const EXPLORE = [
  { label: "NEW", to: "/all-products" },
  { label: "FALL/WINTER 26", to: "/collections/fall-winter-26" },
  { label: "EDITORIAL", to: "/editorial" },
  { label: "ABOUT", to: "/about" },
  { label: "CONTACT", to: "/contact" },
];

const tid = (label) => `menu-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

const Group = ({ group, onClose }) => (
  <div>
    <p className="text-[13px] font-semibold tracking-[0.14em] mb-3">{group.title}</p>
    <ul className="space-y-[7px]">
      {group.links.map((l) => (
        <li key={l.to}>
          <Link
            to={l.to}
            onClick={onClose}
            data-testid={tid(l.label)}
            className="block text-[12px] tracking-[0.06em] uppercase hover:underline underline-offset-4 decoration-1"
          >
            {l.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

export const MenuOverlay = ({ open, onClose, onSearch }) => {
  const [q, setQ] = useState("");
  const [tops, bottoms, headgear, silver] = MENU_GROUPS;

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (window.__lenis) open ? window.__lenis.stop() : window.__lenis.start();
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => {
      document.body.style.overflow = "";
      window.__lenis?.start();
      window.removeEventListener("keydown", esc);
    };
  }, [open, onClose]);

  const submit = (e) => {
    e.preventDefault();
    onSearch(q.trim());
    setQ("");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="menu-overlay"
          className="fixed inset-0 z-50 flex items-stretch md:items-center justify-center md:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <div
            className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
            onClick={onClose}
            data-testid="menu-backdrop"
          />
          <motion.div
            className="relative bg-black text-white w-full md:w-[min(1140px,92vw)] h-full md:h-auto md:max-h-[88vh] overflow-y-auto px-5 py-6 md:px-8 md:py-8 flex flex-col"
            initial={{ opacity: 0, scale: 0.97, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 6 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
          >
            {/* Top row: framed logo + HOME + close */}
            <div className="grid grid-cols-[1fr_auto] md:grid-cols-[minmax(0,1.9fr)_minmax(0,1.3fr)] gap-6 items-start">
              <Link
                to="/"
                onClick={onClose}
                data-testid="menu-logo-link"
                className="inline-block w-full max-w-[370px] hover:opacity-70 transition-opacity"
              >
                <img src={LOGO_WHITE_URL} alt="Rich Kids Funeral" className="h-8 md:h-12 w-auto max-w-full" />
              </Link>
              <div className="flex items-start justify-between gap-6">
                <Link
                  to="/"
                  onClick={onClose}
                  data-testid="menu-link-home"
                  className="hidden md:block text-[15px] tracking-[0.04em] underline underline-offset-4 decoration-1 hover:opacity-60 transition-opacity"
                >
                  HOME
                </Link>
                <button
                  data-testid="menu-close-button"
                  onClick={onClose}
                  aria-label="Close menu"
                  className="ml-auto hover:rotate-90 transition-transform duration-300"
                >
                  <X className="w-7 h-7" strokeWidth={1.25} />
                </button>
              </div>
            </div>

            {/* Link columns */}
            <div className="grid grid-cols-2 md:grid-cols-[minmax(0,0.95fr)_minmax(0,0.95fr)_minmax(0,1.3fr)] gap-x-6 gap-y-10 mt-10 md:mt-12">
              <div className="space-y-9">
                <Group group={tops} onClose={onClose} />
                <Group group={silver} onClose={onClose} />
              </div>
              <div className="space-y-9">
                <Group group={bottoms} onClose={onClose} />
                <Group group={headgear} onClose={onClose} />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Group group={{ title: "EXPLORE", links: EXPLORE }} onClose={onClose} />
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="menu-link-instagram"
                  className="block mt-[7px] text-[12px] tracking-[0.06em] uppercase hover:underline underline-offset-4 decoration-1"
                >
                  INSTAGRAM
                </a>
              </div>
            </div>

            {/* Bottom: VIEW ALL + search */}
            <div className="mt-auto pt-14 md:pt-20 md:grid md:grid-cols-[minmax(0,1.9fr)_minmax(0,1.3fr)] md:gap-6">
              <div className="hidden md:block" />
              <div>
                <Link
                  to="/all-products"
                  onClick={onClose}
                  data-testid="menu-link-view-all"
                  className="inline-block text-[15px] tracking-[0.04em] underline underline-offset-4 decoration-1 hover:opacity-60 transition-opacity"
                >
                  VIEW ALL
                </Link>
                <form onSubmit={submit} className="mt-2 flex" data-testid="menu-search-form">
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search products..."
                    aria-label="Search products"
                    data-testid="menu-search-input"
                    className="flex-1 min-w-0 bg-white text-black text-[15px] px-3 py-2.5 outline-none placeholder:text-black/50"
                  />
                  <button
                    type="submit"
                    data-testid="menu-search-submit"
                    className="bg-white text-black text-[13px] font-semibold tracking-[0.08em] px-4 border-l-2 border-black hover:bg-black hover:text-white hover:border-white transition-colors"
                  >
                    SEARCH
                  </button>
                </form>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
